/* Sections fade up as they enter the viewport.

   IntersectionObserver, not a scroll listener: a scroll handler runs on every
   frame and is the usual reason a page stutters on a phone.

   The class that hides elements is added here, to <html>, so the page is
   fully visible whenever this script does not run — blocked, failed, or an
   old browser. Anyone who asked for reduced motion is opted out before the
   class is ever set, so nothing is hidden for them either. */

function ismReveal() {
    const targets = document.querySelectorAll('[data-reveal]');
    if (!targets.length) return;
    if (!('IntersectionObserver' in window)) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    document.documentElement.classList.add('ism-reveal-on');

    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            entry.target.classList.add('is-revealed');
            observer.unobserve(entry.target);
        });
    }, { rootMargin: '0px 0px -8%' });

    targets.forEach((el) => observer.observe(el));
}

/* Numbers that count up when they first come into view.

   The FINAL value is what the template renders, so the page is correct with
   this script blocked, failed, or opted out — the animation replaces a right
   number with the same right number. That is the whole safety design here:
   never render 0 and fill it in, because the failure mode of that is a practice
   telling visitors it has no practitioners.

   `data-ism-count` marks an element as animatable and nothing else; the value
   is read from the element's own text, so the two can never disagree. */
function ismCounters() {
    const targets = document.querySelectorAll('[data-ism-count]');
    if (!targets.length) return;
    if (!('IntersectionObserver' in window)) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (!entry.isIntersecting) return;
            observer.unobserve(entry.target);

            const el = entry.target;
            const to = parseInt(el.textContent.replace(/\D/g, ''), 10);
            /* Not a number, or one small enough that counting to it is a twitch
               rather than a move: leave it exactly as the template wrote it. */
            if (!Number.isFinite(to) || to < 5) return;

            const started = performance.now();
            const step = (now) => {
                const p = Math.min((now - started) / 900, 1);
                /* Ease out cubic: fast at the start, settling on the value,
                   which is how a counter reads as arriving rather than ticking. */
                el.textContent = Math.round(to * (1 - Math.pow(1 - p, 3)));
                if (p < 1) requestAnimationFrame(step);
            };
            requestAnimationFrame(step);
        });
    }, { threshold: 0.4 });

    targets.forEach(el => observer.observe(el));
}

/* The hero photograph drifts slower than the page, and the title arrives word by
   word. Both are motion only — the page is complete and correct without either,
   which is why they live here behind the same reduced-motion door as the rest.

   The parallax moves the MEDIA layer, which carries the scrim with it: the
   photograph and its grading are one object, so the relationship the hero's
   contrast table measured travels with them. Moving the image alone would slide
   a lit corridor under the type at some scroll offset nobody measured. */
function ismHeroMotion() {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const key = document.querySelector('.ism-hero-title-key');
    if (key && !key.querySelector('.ism-hero-word')) {
        /* Split on whitespace and rejoin with a real space between the spans.
           The spaces are not decoration: this heading's accessible name is
           flattened from its children, and spans laid out edge to edge would
           give a screen reader "Centremédical ISM". */
        const words = key.textContent.trim().split(/\s+/);
        key.textContent = '';
        words.forEach((word, i) => {
            const span = document.createElement('span');
            span.className = 'ism-hero-word';
            span.style.setProperty('--ism-word-i', i);
            span.textContent = word;
            key.appendChild(span);
            if (i < words.length - 1) key.appendChild(document.createTextNode(' '));
        });
        key.classList.add('is-split');
        /* The heading, not just the key, because the rule that has to stand
           down is the one on `.ism-hero-inner > *` — and that selects the h1. */
        key.closest('.ism-hero-title')?.classList.add('is-split');
    }

    const media = document.querySelector('.ism-hero-media');
    const hero = document.querySelector('.ism-hero');
    if (!media || !hero) return;

    let ticking = false;
    const place = () => {
        ticking = false;
        /* Stop once the hero has left: past its own height the offset is not
           read by anyone and the work is pure scroll cost. */
        const travel = Math.min(window.scrollY, hero.offsetHeight);
        media.style.translate = '0 ' + (travel * 0.14).toFixed(1) + 'px';
    };
    addEventListener('scroll', () => {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(place);
    }, { passive: true });
    place();
}
